import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList, Plus, Search, User, Building2, Calendar, Clock,
  CheckCircle2, AlertTriangle, Loader2, MoreVertical, Trash2,
  MessageSquare, Send, Filter, ChevronDown, ChevronUp, Layers, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import api, { propertiesAPI, clientsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  a_faire: { label: 'A faire', color: 'bg-gray-100 text-gray-700', icon: Clock },
  en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  termine: { label: 'Termine', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
};

const PRIORITY_CONFIG = {
  faible: { label: 'Faible', color: 'bg-gray-100 text-gray-600' },
  normale: { label: 'Normale', color: 'bg-blue-100 text-blue-700' },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-700' },
};

const CATEGORY_CONFIG = {
  general: { label: 'General', color: 'bg-gray-100 text-gray-600' },
  visite: { label: 'Visite', color: 'bg-purple-100 text-purple-700' },
  recouvrement: { label: 'Recouvrement', color: 'bg-amber-100 text-amber-700' },
  maintenance: { label: 'Maintenance', color: 'bg-orange-100 text-orange-700' },
  administratif: { label: 'Administratif', color: 'bg-teal-100 text-teal-700' },
};

// Modules de la plateforme : pour qu'un admin puisse rattacher une tache a un module precis
const PLATFORM_MODULES = [
  { value: 'properties', label: 'Biens', path: '/properties' },
  { value: 'clients', label: 'Clients', path: '/clients' },
  { value: 'transactions', label: 'Transactions', path: '/transactions' },
  { value: 'rent_payments', label: 'Paiements Loyer', path: '/rent-payments' },
  { value: 'land_sales', label: 'Ventes Terrains', path: '/land-sales' },
  { value: 'due_notices', label: 'Avis Echeance', path: '/due-notices' },
  { value: 'all_receipts', label: 'Toutes les Quittances', path: '/all-receipts' },
  { value: 'property_inspections', label: 'Etats des Lieux', path: '/property-inspections' },
  { value: 'property_visits', label: 'Visites de Biens', path: '/property-visits' },
  { value: 'key_handovers', label: 'Remise de Cles', path: '/key-handovers' },
  { value: 'repairs', label: 'Reparations', path: '/repairs' },
  { value: 'repair_requests', label: 'Demandes Reparations', path: '/repair-requests' },
  { value: 'agency_expenses', label: "Frais d'agence", path: '/agency-expenses' },
  { value: 'financial_statements', label: 'Releves Financiers', path: '/financial-statements' },
  { value: 'owner_balance', label: 'Bilan Proprietaires', path: '/owner-balance' },
  { value: 'agency_balance', label: 'Bilan Agence', path: '/agency-balance' },
  { value: 'security_deposits', label: 'Depots de Garantie', path: '/security-deposits' },
  { value: 'tenant_accounts', label: 'Acces Locataires', path: '/tenant-accounts' },
  { value: 'owner_access', label: 'Acces Proprietaires', path: '/owner-access' },
  { value: 'tenant_requests', label: 'Demandes Locataires', path: '/tenant-requests' },
  { value: 'users', label: 'Utilisateurs', path: '/users' },
  { value: 'activity_logs', label: 'Tracabilite', path: '/activity-logs' },
  { value: 'document_templates', label: 'Modeles Documents', path: '/document-templates' },
  { value: 'time_tracking', label: "Releve d'Heures", path: '/time-tracking' },
  { value: 'payslips', label: 'Bulletins de Paie', path: '/payslips' },
  { value: 'shareholders', label: 'Actionnariat', path: '/shareholders' },
  { value: 'general_assemblies', label: 'Assemblees Generales', path: '/general-assemblies' },
  { value: 'agent_permissions', label: 'Permissions Agents', path: '/agent-permissions' },
  { value: 'agent_deposits', label: 'Versements Agents', path: '/agent-deposits' },
  { value: 'qr_codes', label: 'QR Codes', path: '/qr-codes' },
  { value: 'construction', label: 'Construction', path: '/construction' },
  { value: 'chat', label: 'Messagerie', path: '/chat' },
  { value: 'training', label: 'Formation', path: '/training' },
  { value: 'agency_settings', label: 'Parametres', path: '/agency-settings' },
];
const MODULE_BY_VALUE = PLATFORM_MODULES.reduce((acc, m) => { acc[m.value] = m; return acc; }, {});

const Tasks = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);

  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', assigned_to: '', priority: 'normale',
    due_date: '', property_id: '', client_id: '', category: 'general', module: ''
  });

  // Detail modal
  const [selectedTask, setSelectedTask] = useState(null);
  const [comment, setComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, statsRes] = await Promise.all([
        api.get('/tasks'),
        api.get('/tasks/stats')
      ]);
      setTasks(tasksRes.data);
      setStats(statsRes.data);

      if (isAdmin) {
        const [usersRes, propsRes, cliRes] = await Promise.all([
          api.get('/users'),
          propertiesAPI.getAll(),
          clientsAPI.getAll()
        ]);
        setAgents(usersRes.data.filter(u => u.role === 'agent' || ['admin', 'super_admin', 'agent'].includes(u.role)));
        setProperties(propsRes.data);
        setClients(cliRes.data);
      }
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.title || !form.assigned_to) {
      toast.error('Titre et agent sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      const selectedModule = form.module && form.module !== '_none' ? MODULE_BY_VALUE[form.module] : null;
      const payload = {
        ...form,
        property_id: form.property_id === '_none' ? '' : form.property_id,
        client_id: form.client_id === '_none' ? '' : form.client_id,
        module: selectedModule ? selectedModule.value : null,
        module_path: selectedModule ? selectedModule.path : null,
      };
      await api.post('/tasks', payload);
      toast.success('Tache creee');
      setShowCreate(false);
      setForm({ title: '', description: '', assigned_to: '', priority: 'normale', due_date: '', property_id: '', client_id: '', category: 'general', module: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
      toast.success(`Statut: ${STATUS_CONFIG[newStatus]?.label}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Supprimer cette tache ?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Tache supprimee');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleAddComment = async (taskId) => {
    if (!comment.trim()) return;
    setSendingComment(true);
    try {
      const res = await api.post(`/tasks/${taskId}/comments`, { content: comment });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, comments: [...(t.comments || []), res.data.comment] } : t));
      setComment('');
      toast.success('Commentaire ajoute');
    } catch {
      toast.error('Erreur');
    } finally {
      setSendingComment(false);
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      if (filterStatus !== 'all' && t.status !== filterStatus) return false;
      if (filterAgent !== 'all' && t.assigned_to !== filterAgent) return false;
      if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
      if (filterModule !== 'all' && t.module !== filterModule) return false;
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        if (!t.title?.toLowerCase().includes(s) && !t.assigned_to_name?.toLowerCase().includes(s) && !t.description?.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [tasks, filterStatus, filterAgent, filterPriority, filterModule, searchTerm]);

  const isOverdue = (task) => {
    if (task.status === 'termine' || !task.due_date) return false;
    return new Date(task.due_date) < new Date();
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Gestion des Taches</h1>
          <p className="text-gray-500 mt-1">Attribuez et suivez les taches de vos agents</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="create-task-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle tache
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total || 0, color: 'text-gray-900', bg: 'bg-gray-50' },
          { label: 'A faire', value: stats.a_faire || 0, color: 'text-gray-600', bg: 'bg-gray-50' },
          { label: 'En cours', value: stats.en_cours || 0, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Terminees', value: stats.termine || 0, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'En retard', value: stats.en_retard || 0, color: 'text-red-600', bg: 'bg-red-50' },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className={`p-4 text-center ${s.bg} rounded-lg`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger><SelectValue placeholder="Statut" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="a_faire">A faire</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="termine">Termine</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger><SelectValue placeholder="Priorite" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes priorites</SelectItem>
                <SelectItem value="urgente">Urgente</SelectItem>
                <SelectItem value="normale">Normale</SelectItem>
                <SelectItem value="faible">Faible</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterModule} onValueChange={setFilterModule}>
              <SelectTrigger data-testid="filter-module"><SelectValue placeholder="Module" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">Tous les modules</SelectItem>
                {PLATFORM_MODULES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={filterAgent} onValueChange={setFilterAgent}>
                <SelectTrigger><SelectValue placeholder="Agent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les agents</SelectItem>
                  {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tasks List */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" /></div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <ClipboardList className="w-14 h-14 mx-auto mb-3 opacity-40" />
          <p className="text-lg">Aucune tache</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map(task => {
            const stCfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.a_faire;
            const priCfg = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normale;
            const catCfg = CATEGORY_CONFIG[task.category] || CATEGORY_CONFIG.general;
            const overdue = isOverdue(task);
            const expanded = expandedTask === task.id;

            return (
              <Card key={task.id} className={`${overdue ? 'border-red-200' : ''} transition-all`} data-testid={`task-${task.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Status indicator */}
                    <div className={`w-2 h-full min-h-[40px] rounded-full flex-shrink-0 ${task.status === 'termine' ? 'bg-green-500' : task.status === 'en_cours' ? 'bg-blue-500' : overdue ? 'bg-red-500' : 'bg-gray-300'}`} />

                    <div className="flex-1 min-w-0">
                      {/* Title row */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="cursor-pointer" onClick={() => setExpandedTask(expanded ? null : task.id)}>
                          <h3 className={`font-medium ${task.status === 'termine' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{task.title}</h3>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge className={stCfg.color + ' text-xs'}>{stCfg.label}</Badge>
                            <Badge className={priCfg.color + ' text-xs'}>{priCfg.label}</Badge>
                            <Badge className={catCfg.color + ' text-xs'}>{catCfg.label}</Badge>
                            {overdue && <Badge className="bg-red-100 text-red-700 text-xs">En retard</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {task.status !== 'en_cours' && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(task.id, 'en_cours')} className="text-blue-600">
                                  <Loader2 className="w-4 h-4 mr-2" /> En cours
                                </DropdownMenuItem>
                              )}
                              {task.status !== 'termine' && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(task.id, 'termine')} className="text-green-600">
                                  <CheckCircle2 className="w-4 h-4 mr-2" /> Terminer
                                </DropdownMenuItem>
                              )}
                              {task.status !== 'a_faire' && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(task.id, 'a_faire')}>
                                  <Clock className="w-4 h-4 mr-2" /> A faire
                                </DropdownMenuItem>
                              )}
                              {isAdmin && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDelete(task.id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      {/* Meta info */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{task.assigned_to_name}</span>
                        {task.module && MODULE_BY_VALUE[task.module] && (
                          <Link
                            to={MODULE_BY_VALUE[task.module].path}
                            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1B3A4B]/10 text-[#1B3A4B] hover:bg-[#1B3A4B]/20 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`task-module-link-${task.id}`}
                            title={`Ouvrir ${MODULE_BY_VALUE[task.module].label}`}
                          >
                            <Layers className="w-3 h-3" />
                            {MODULE_BY_VALUE[task.module].label}
                            <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                          </Link>
                        )}
                        {task.due_date && (
                          <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}>
                            <Calendar className="w-3 h-3" />Echeance: {new Date(task.due_date).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                        {task.property_title && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{task.property_title}</span>}
                        {task.client_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />{task.client_name}</span>}
                        {task.comments?.length > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{task.comments.length}</span>}
                      </div>

                      {/* Expanded details */}
                      {expanded && (
                        <div className="mt-3 pt-3 border-t space-y-3">
                          {task.description && <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{task.description}</p>}

                          {/* Comments */}
                          {task.comments?.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-gray-500">Commentaires ({task.comments.length})</p>
                              {task.comments.map((c, i) => (
                                <div key={c.id || i} className="bg-gray-50 rounded p-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-[#1B3A4B]">{c.author_name}</span>
                                    <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString('fr-FR')}</span>
                                  </div>
                                  <p className="text-sm text-gray-700 mt-1">{c.content}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add comment */}
                          <div className="flex gap-2">
                            <Input placeholder="Ajouter un commentaire..." value={comment} onChange={e => setComment(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') handleAddComment(task.id); }}
                              className="text-sm" data-testid={`comment-input-${task.id}`} />
                            <Button size="sm" onClick={() => handleAddComment(task.id)} disabled={sendingComment || !comment.trim()}
                              className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid={`send-comment-${task.id}`}>
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Task Modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Nouvelle tache</DialogTitle>
            <DialogDescription>Attribuez une tache a un agent</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Titre *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Faire visiter l'immeuble X" data-testid="task-title" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Details..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Agent *</Label>
                <Select value={form.assigned_to} onValueChange={v => setForm(f => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger data-testid="task-agent"><SelectValue placeholder="Choisir un agent" /></SelectTrigger>
                  <SelectContent>
                    {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorite</Label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="faible">Faible</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categorie</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="visite">Visite</SelectItem>
                    <SelectItem value="recouvrement">Recouvrement</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="administratif">Administratif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Echeance</Label>
                <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="task-due-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Bien (optionnel)</Label>
                <Select value={form.property_id || '_none'} onValueChange={v => setForm(f => ({ ...f, property_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Aucun</SelectItem>
                    {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client (optionnel)</Label>
                <Select value={form.client_id || '_none'} onValueChange={v => setForm(f => ({ ...f, client_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Aucun</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-[#1B3A4B]" />
                Module concerne (optionnel)
              </Label>
              <Select value={form.module || '_none'} onValueChange={v => setForm(f => ({ ...f, module: v === '_none' ? '' : v }))}>
                <SelectTrigger data-testid="task-module"><SelectValue placeholder="Selectionner un module de la plateforme" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="_none">Aucun</SelectItem>
                  {PLATFORM_MODULES.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                L'agent verra un lien direct vers ce module depuis la fiche de tache.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="confirm-create-task">
              {saving ? 'Creation...' : 'Creer la tache'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tasks;
